// ensureBusiness — the ONE writer that makes the business exist (Review
// Focus 5). Whichever screen ends the business's setup inputs (hours, work
// days, handover, or the last business question for seats) awaits it:
//
//   1 createdId ?? profile POST /v1/business → kept in the wizard's memory
//   2 booking PUT /v1/business/{id}/scheduling with the shape — NOT best effort
//   3 branches.multi && !branchesApplied → profile GET …/branches → PATCH the
//     is_main row {name: firstName} → branchesApplied
//
// Every step re-runs safely: createdId guards 1, branchesApplied guards 3,
// and 2 is a PUT. Errors propagate to the screen, which maps them (field
// errors reopen their question; otherwise the error card).

import { onboardingStore, toMinutes } from '../state';
import { createBusiness, fieldErrors, listBranches, patchBranch, putScheduling } from './api';
import { ONB } from './flow';
import { SHAPES, schedulingBody, shapeOf } from './shapes';
import type { BusinessType } from './api';

type FieldKey = keyof ReturnType<typeof fieldErrors>;

/** The first refused field decides which question reopens (draft order). */
const FIELD_ROUTE: Array<[FieldKey, string]> = [
  ['name', ONB.name],
  ['slug', ONB.name],
  ['category', ONB.type],
  ['city', ONB.city],
  ['area', ONB.address],
  ['phone', ONB.bphone],
];

/**
 * A field profile refused on the create belongs to its own question: the
 * route to reopen with the server's sentence — only while the business does
 * not exist yet (afterwards the answers are locked). null = not a field error.
 */
export function reopenRoute(err: unknown): { route: string; message: string } | null {
  if (onboardingStore.get().createdId) return null;
  const fields = fieldErrors(err);
  const hit = FIELD_ROUTE.find(([k]) => !!fields[k]);
  return hit ? { route: hit[1], message: fields[hit[0]] ?? '' } : null;
}

/** The screen that calls ensureBusiness for this category: its step 5, or the business phone for seats. */
export function writingRoute(type: Pick<BusinessType, 'booking_shape' | 'default_scheduling_mode'> | null | undefined): string {
  const step5 = SHAPES[shapeOf(type) ?? 'staff'].step5;
  return step5 === 'none' ? ONB.bphone : ONB[step5];
}

/**
 * Where a business that exists but whose writing step never finished belongs
 * (a failure, then a reload): back to the screen that writes it. null =
 * nothing pending. The writing step (useEnrollRun) sets `scheduled` only once
 * the scheduling PUT, the first branch's name and its own write all succeeded.
 */
export function unscheduledRoute(): string | null {
  const s = onboardingStore.get();
  return s.createdId && !s.scheduled ? writingRoute(s.type) : null;
}

/** The shape step is finished (its minimum met, or staff skipped): Done may show. */
export function finishShape(navigate: (to: string) => void): void {
  onboardingStore.set({ shapeDone: true });
  navigate(ONB.done);
}

/** Where an existing, written business goes next: the shape step until its minimum is met (R8), then done. */
export function afterWriting(): string {
  return onboardingStore.get().shapeDone ? ONB.done : ONB.shape;
}

export async function ensureBusiness(): Promise<string> {
  let id = onboardingStore.get().createdId;
  if (!id) {
    const { draft, slug } = onboardingStore.get();
    const created = await createBusiness(draft, slug);
    id = created.id;
    onboardingStore.set({ createdId: id, slug: created.slug ?? slug });
  }

  const s = onboardingStore.get();
  const shape = shapeOf(s.type) ?? 'staff';
  await putScheduling(
    id,
    schedulingBody(shape, s.type, {
      checkInMin: toMinutes(s.handover.checkIn) ?? undefined,
      checkOutMin: toMinutes(s.handover.checkOut) ?? undefined,
      sittingMin: s.sittingMin,
    }),
  );

  const { branches, branchesApplied } = onboardingStore.get();
  const firstName = branches.firstName.trim();
  if (branches.multi && firstName && !branchesApplied) {
    const list = await listBranches(id);
    const main = list.find((b) => b.is_main);
    // Applied only once the main row carries the name (no main row: left for the next run).
    if (main) {
      if (main.name !== firstName) await patchBranch(id, main.id, { name: firstName });
      onboardingStore.set({ branchesApplied: true });
    }
  }
  return id;
}
